import { useEffect } from 'react'
import { X, Users } from 'lucide-react'
import { usePropertyProfileStore } from '@/store/propertyProfileStore'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { NarrativesPanel } from '@/components/deal/NarrativesPanel'
import type { PropertyProfile } from '@/types/propertyProfile'
import { PropertyTab, SponsorTab, type SponsorProfile } from '@/components/shared/propertyProfileRenderers'
import {
  PHOTO_SET_MULTIFAMILY,
  PHOTO_SET_INDUSTRIAL,
  PHOTO_SET_MULTIFAMILY_MIDRISE,
  PHOTO_SET_HOSPITALITY,
  PHOTO_SET_LOGISTICS_ALT,
  PHOTO_SET_LUXURY_MF,
} from '@/data/localPropertyPhotoSets'
import { ANTARCTICA_GAP_PROPERTY_PROFILES, COMMAND_CENTER_PROPERTY_PROFILES } from '@/data/generatedPropertyProfiles'

/* ═══════════════════════════════════════════════════════════════════════════
   PROPERTY PROFILES — keyed by deal ID
   ═══════════════════════════════════════════════════════════════════════════ */

const CORE_PROPERTY_PROFILES: Record<string, PropertyProfile> = {
  'ALG-1001': {
    dealId: 'ALG-1001',
    propertyName: 'Harbor Point Residences',
    tagline: '340-Unit Class A Multifamily',
    location: 'Miami, FL 33131',
    fullAddress: '1200 Brickell Bay Drive, Miami, FL 33131',
    loanAmount: '$62.5M',
    product: 'Senior Mortgage',
    stage: 'Triage',
    propertyType: 'Multifamily',
    yearBuilt: 2019,
    totalUnits: 340,
    totalSF: '289,000',
    avgUnitSize: '850 SF',
    stories: 28,
    parking: '510 spaces (1.5:1)',
    amenities: 'Resort Pool, Fitness Center, Rooftop Lounge, Co-Working, Pet Spa, EV Charging',
    zoning: 'T6-24A Urban Core',
    parcel: '01-4152-027-0010',
    lastSale: '$71.4M (2021)',
    photos: PHOTO_SET_MULTIFAMILY,
    mapLat: 25.7617,
    mapLng: -80.1918,
    population3mi: '312,450',
    medianHHI: '$82,300',
    popGrowth: '+3.1%',
    walkScore: '89/100',
    crimeIndex: 'Low',
    empGrowth: '+3.8%',
    topEmployers: ['Baptist Health South Florida', 'FPL Group', 'Royal Caribbean Group', 'Carnival Corporation', 'Citadel Securities'],
    schools: 'A-rated Miami-Dade district',
    transit: 'Metromover 0.2mi, Metrorail 0.5mi, I-95 0.8mi',
    nearbyDevelopments: '3 projects under construction within 1mi (1,420 units total)',
    submarket: 'Brickell / Downtown Miami Multifamily',
    submarketVacancy: '4.8%',
    submarketRentGrowth: '+3.7%',
    submarketAbsorption: '2,850 units/yr',
    revenue: '$9.29M',
    expenses: '$3.53M',
    noi: '$5.76M',
    capRate: '6.45%',
    occupancy: '96.2%',
    expenseRatio: '38.0%',
    revPerUnit: '$27,324/yr',
    expPerUnit: '$10,382/yr',
    noiPerUnit: '$16,941/yr',
    unitMix: [
      { type: 'Studio (68)', count: 68, avgRent: 2000, occupancy: 97 },
      { type: '1BR (170)', count: 170, avgRent: 2250, occupancy: 96 },
      { type: '2BR (102)', count: 102, avgRent: 2750, occupancy: 95 },
    ],
    avgRentUnit: '$2,335/mo',
    avgRentSF: '$2.75/SF',
    leaseRenewalRate: '74%',
    avgLeaseTerm: '13 months',
    concessions: '$450 avg move-in',
    leaseExpirations: [
      { year: '2026 H1', sf: 86700, pctOfTotal: 30 },
      { year: '2026 H2', sf: 72250, pctOfTotal: 25 },
      { year: '2027 H1', sf: 57800, pctOfTotal: 20 },
      { year: '2027 H2', sf: 43350, pctOfTotal: 15 },
      { year: '2028+', sf: 28900, pctOfTotal: 10 },
    ],
    contacts: [
      { role: 'Property Manager', name: 'Maria Santos', company: 'Greystar', phone: '(305) 555-0142', email: 'msantos@greystar.com' },
      { role: 'Leasing Director', name: 'Carlos Rivera', company: 'On-site', phone: '(305) 555-0198', email: 'crivera@harborpoint.com' },
      { role: 'Insurance Broker', name: 'Tom Wright', company: 'Alliant Insurance', phone: '(305) 555-0267', email: 'twright@alliant.com' },
    ],
    comparables: [
      { name: 'Biscayne Bay Towers', units: 310, avgRent: 2280, capRate: 5.1, distance: '0.4mi' },
      { name: 'Midtown Miami Lofts', units: 195, avgRent: 2450, capRate: 5.3, distance: '1.2mi' },
      { name: 'Edgewater Residences', units: 340, avgRent: 2200, capRate: 5.0, distance: '0.8mi' },
    ],
    compSummary: 'Rent: +3% above avg | Cap Rate: In line | Occupancy: Above submarket avg',
    lastInspection: 'Feb 15, 2026',
    inspector: 'Partner Engineering',
    overallCondition: 'Excellent (A-)',
    immediateRepairs: '$85,000',
    deferredMaintenance: '$240,000 over 5 years',
    environmental: 'Phase I ESA Complete - No RECs identified',
    seismicZone: 'Low Risk',
    floodZone: 'X (Minimal Flood Risk)',
    sponsorId: 'harbor-dev-group',
  },

  'ALG-1003': {
    dealId: 'ALG-1003',
    propertyName: 'Gateway Logistics Center',
    tagline: '1.24M SF Class A Industrial/Logistics',
    location: 'Phoenix, AZ 85043',
    fullAddress: '4500 W Buckeye Rd, Phoenix, AZ 85043',
    loanAmount: '$145.0M',
    product: 'Large Loan',
    stage: 'Triage',
    propertyType: 'Industrial',
    yearBuilt: 2022,
    totalSF: '1,240,000',
    stories: 1,
    parking: '480 trailer stalls, 1,200 car spaces',
    amenities: '40ft Clear Height, ESFR Sprinklers, Cross-Dock, 200+ Dock Doors',
    zoning: 'I-2 Heavy Industrial',
    parcel: '105-42-001B',
    lastSale: '$178.5M (2023)',
    photos: PHOTO_SET_INDUSTRIAL,
    mapLat: 33.4340,
    mapLng: -112.1250,
    population3mi: '98,200',
    medianHHI: '$56,800',
    popGrowth: '+4.2%',
    walkScore: '32/100',
    crimeIndex: 'Moderate',
    empGrowth: '+5.1%',
    topEmployers: ['Amazon', 'Intel', 'Banner Health', 'Honeywell', 'Republic Services'],
    schools: 'B-rated Tolleson district',
    transit: 'I-10 0.3mi, Sky Harbor Airport 12mi, UP Rail Spur adjacent',
    nearbyDevelopments: '4.2M SF under construction within 5mi corridor',
    submarket: 'Southwest Phoenix Industrial',
    submarketVacancy: '6.1%',
    submarketRentGrowth: '+4.8%',
    submarketAbsorption: '8,200,000 SF/yr',
    revenue: '$15.42M',
    expenses: '$3.39M',
    noi: '$12.03M',
    capRate: '5.81%',
    occupancy: '89.5%',
    expenseRatio: '22.0%',
    revPerUnit: '$12.44/SF',
    expPerUnit: '$2.73/SF',
    noiPerUnit: '$9.70/SF',
    tenants: [
      { tenant: 'Amazon Logistics', suite: 'Bldg A', sf: 520000, rentMo: 520000, leaseStart: '2022-09', leaseEnd: '2032-08', status: 'Current' },
      { tenant: 'FedEx Ground', suite: 'Bldg B', sf: 380000, rentMo: 360500, leaseStart: '2023-01', leaseEnd: '2033-12', status: 'Current' },
      { tenant: 'XPO Logistics', suite: 'Bldg C', sf: 210000, rentMo: 210000, leaseStart: '2024-03', leaseEnd: '2031-02', status: 'Current' },
      { tenant: 'Vacant', suite: 'Bldg D (partial)', sf: 130000, rentMo: 0, leaseStart: '--', leaseEnd: '--', status: 'Vacant' },
    ],
    avgRentSF: '$11.80/SF NNN',
    leaseExpirations: [
      { year: '2029-2030', sf: 210000, pctOfTotal: 17 },
      { year: '2031-2032', sf: 520000, pctOfTotal: 42 },
      { year: '2033+', sf: 380000, pctOfTotal: 31 },
      { year: 'Vacant', sf: 130000, pctOfTotal: 10 },
    ],
    contacts: [
      { role: 'Property Manager', name: 'Kevin Tran', company: 'Prologis Mgmt', phone: '(602) 555-0311', email: 'ktran@prologis.com' },
      { role: 'Leasing Broker', name: 'Jessica Palmer', company: 'CBRE', phone: '(602) 555-0445', email: 'jpalmer@cbre.com' },
      { role: 'Insurance Broker', name: 'David Stein', company: 'Marsh McLennan', phone: '(602) 555-0558', email: 'dstein@marsh.com' },
    ],
    comparables: [
      { name: 'Loop 303 Logistics Park', sf: 980000, avgRent: 11.50, capRate: 5.6, distance: '3.2mi' },
      { name: 'Goodyear Distribution Ctr', sf: 1500000, avgRent: 12.10, capRate: 5.4, distance: '8.5mi' },
      { name: 'Tolleson Commerce Hub', sf: 720000, avgRent: 11.90, capRate: 5.8, distance: '1.8mi' },
    ],
    compSummary: 'Rent: In line | Cap Rate: Slightly above comps | Vacancy: Above submarket (lease-up in Bldg D)',
    lastInspection: 'Jan 28, 2026',
    inspector: 'EBI Consulting',
    overallCondition: 'Excellent (A)',
    immediateRepairs: '$45,000',
    deferredMaintenance: '$180,000 over 5 years',
    environmental: 'Phase I ESA Complete - No RECs. Vapor mitigation system in Bldg A (historical)',
    seismicZone: 'Low Risk',
    floodZone: 'X (Minimal)',
    sponsorId: 'greenfield-re',
  },

  'ALG-1004': {
    dealId: 'ALG-1004',
    propertyName: 'The Sinclair on Peachtree',
    tagline: '230-Unit Class A Multifamily',
    location: 'Atlanta, GA 30309',
    fullAddress: '1555 Peachtree St NE, Atlanta, GA 30309',
    loanAmount: '$38.5M',
    product: 'Senior Mortgage',
    stage: 'Post-screen Quote',
    propertyType: 'Multifamily',
    yearBuilt: 2016,
    totalUnits: 230,
    totalSF: '195,500',
    avgUnitSize: '850 SF',
    stories: 6,
    parking: '345 spaces (1.5:1)',
    amenities: 'Saltwater Pool, Dog Park, Fitness Studio, Coworking Lounge, Outdoor Kitchen',
    zoning: 'MRC-3 Mixed Residential Commercial',
    parcel: '17-0063-0002-058',
    lastSale: '$42.8M (2019)',
    photos: PHOTO_SET_MULTIFAMILY_MIDRISE,
    mapLat: 33.7900,
    mapLng: -84.3880,
    population3mi: '185,600',
    medianHHI: '$71,200',
    popGrowth: '+2.4%',
    walkScore: '78/100',
    crimeIndex: 'Moderate',
    empGrowth: '+2.9%',
    topEmployers: ['Coca-Cola', 'Delta Air Lines', 'Home Depot', 'UPS', 'Emory Healthcare'],
    schools: 'B+-rated Atlanta Public Schools',
    transit: 'MARTA Arts Center 0.4mi, I-75/85 0.6mi',
    nearbyDevelopments: '2 projects under construction within 0.5mi (680 units)',
    submarket: 'Midtown Atlanta Multifamily',
    submarketVacancy: '6.3%',
    submarketRentGrowth: '+2.9%',
    submarketAbsorption: '3,100 units/yr',
    revenue: '$6.05M',
    expenses: '$2.42M',
    noi: '$3.63M',
    capRate: '6.60%',
    occupancy: '94.8%',
    expenseRatio: '40.0%',
    revPerUnit: '$26,304/yr',
    expPerUnit: '$10,522/yr',
    noiPerUnit: '$15,783/yr',
    unitMix: [
      { type: '1BR (138)', count: 138, avgRent: 1800, occupancy: 95 },
      { type: '2BR (92)', count: 92, avgRent: 2100, occupancy: 94 },
    ],
    avgRentUnit: '$1,920/mo',
    avgRentSF: '$2.26/SF',
    leaseRenewalRate: '68%',
    avgLeaseTerm: '12 months',
    concessions: '$300 avg move-in',
    leaseExpirations: [
      { year: '2026 H2', sf: 58650, pctOfTotal: 30 },
      { year: '2027 H1', sf: 48875, pctOfTotal: 25 },
      { year: '2027 H2', sf: 39100, pctOfTotal: 20 },
      { year: '2028 H1', sf: 29325, pctOfTotal: 15 },
      { year: '2028+', sf: 19550, pctOfTotal: 10 },
    ],
    contacts: [
      { role: 'Property Manager', name: 'Angela Washington', company: 'Lincoln Property Co.', phone: '(404) 555-0221', email: 'awashington@lincolnapts.com' },
      { role: 'Leasing Agent', name: 'Tyler Brooks', company: 'On-site', phone: '(404) 555-0334', email: 'tbrooks@sinclairpeachtree.com' },
      { role: 'Insurance Broker', name: 'Rachel Nguyen', company: 'HUB International', phone: '(404) 555-0478', email: 'rnguyen@hubinternational.com' },
    ],
    comparables: [
      { name: 'The Residence at Midtown', units: 280, avgRent: 1850, capRate: 5.4, distance: '0.6mi' },
      { name: 'Colony Square Living', units: 190, avgRent: 2050, capRate: 5.2, distance: '0.3mi' },
      { name: 'Peachtree Heights Apts', units: 320, avgRent: 1780, capRate: 5.6, distance: '1.1mi' },
    ],
    compSummary: 'Rent: +2% above avg | Cap Rate: Slightly above comps | Solid Midtown location',
    lastInspection: 'Mar 02, 2026',
    inspector: 'EMG Corp',
    overallCondition: 'Good (B+)',
    immediateRepairs: '$110,000',
    deferredMaintenance: '$295,000 over 5 years',
    environmental: 'Phase I ESA Complete - No RECs identified',
    seismicZone: 'Low Risk',
    floodZone: 'X (Minimal)',
    sponsorId: 'crescent-multifamily',
  },

  'ALG-1005': {
    dealId: 'ALG-1005',
    propertyName: 'The Langford Hotel',
    tagline: '126-Key Boutique Hotel, Marriott Autograph Collection',
    location: 'Miami, FL 33130',
    fullAddress: '121 SE 1st St, Miami, FL 33130',
    loanAmount: '$18.5M',
    product: 'B-Note',
    stage: 'Post-screen Quote',
    propertyType: 'Hospitality',
    yearBuilt: 1926,
    totalSF: '142,000',
    stories: 4,
    parking: '0 on-site (valet service)',
    amenities: 'Rooftop Bar & Pool, Full-Service Restaurant, Ballroom, Spa, Historic Landmark',
    zoning: 'T6-12 Urban Core',
    parcel: '01-0102-030-0040',
    lastSale: '$58.0M (2020)',
    photos: PHOTO_SET_HOSPITALITY,
    mapLat: 25.7743,
    mapLng: -80.1937,
    population3mi: '285,100',
    medianHHI: '$74,800',
    popGrowth: '+2.9%',
    walkScore: '95/100',
    crimeIndex: 'Moderate',
    empGrowth: '+3.4%',
    topEmployers: ['Baptist Health', 'University of Miami', 'Royal Caribbean', 'Norwegian Cruise', 'World Fuel Services'],
    schools: 'A-rated Miami-Dade district',
    transit: 'Metromover 0.1mi, Metrorail 0.3mi, PortMiami 2mi',
    nearbyDevelopments: '1 hotel under construction (220 keys) within 0.5mi',
    submarket: 'Downtown Miami Hospitality',
    submarketVacancy: '22.5% (industry-standard RevPAR metric)',
    submarketRentGrowth: '+1.8% ADR growth',
    submarketAbsorption: '650 new keys/yr',
    revenue: '$16.00M',
    expenses: '$9.92M',
    noi: '$6.08M',
    capRate: '7.79%',
    occupancy: '77.5%',
    expenseRatio: '62.0%',
    revPerUnit: '$126,984/key/yr',
    expPerUnit: '$78,730/key/yr',
    noiPerUnit: '$48,254/key/yr',
    tenants: [
      { tenant: 'Marriott Autograph (126 keys)', suite: 'Entire Property', sf: 142000, rentMo: 1200000, leaseStart: '2020-01', leaseEnd: '2040-12', status: 'Current' },
    ],
    avgRentSF: 'ADR: $289 | RevPAR: $224',
    leaseExpirations: [
      { year: 'Franchise Exp 2040', sf: 142000, pctOfTotal: 100 },
    ],
    contacts: [
      { role: 'General Manager', name: 'Isabella Morales', company: 'Marriott International', phone: '(305) 555-0511', email: 'imorales@marriott.com' },
      { role: 'Asset Manager', name: 'James Park', company: 'Riviera Hospitality', phone: '(305) 555-0622', email: 'jpark@rivierahospitality.com' },
      { role: 'Insurance Broker', name: 'Sandra Lee', company: 'Lockton Companies', phone: '(305) 555-0733', email: 'slee@lockton.com' },
    ],
    comparables: [
      { name: 'The Gabriel Miami', units: 129, avgRent: 302, capRate: 7.5, distance: '0.3mi' },
      { name: 'Arlo Wynwood', units: 217, avgRent: 245, capRate: 8.1, distance: '2.1mi' },
      { name: 'Casa Faena', units: 169, avgRent: 395, capRate: 6.8, distance: '4.5mi' },
    ],
    compSummary: 'ADR: In line with boutique comp set | RevPAR: Above avg | Strong brand affiliation',
    lastInspection: 'Jan 10, 2026',
    inspector: 'HVS Consulting',
    overallCondition: 'Good (B+) - Recent $12M renovation (2019)',
    immediateRepairs: '$180,000 (HVAC upgrades)',
    deferredMaintenance: '$1.2M PIP over 5 years',
    environmental: 'Phase I ESA Complete - Historical dry cleaner adjacent (no impact)',
    seismicZone: 'Low Risk',
    floodZone: 'AE (Flood insurance required)',
    sponsorId: 'pacific-coast-hospitality',
  },

  'ALG-1007': {
    dealId: 'ALG-1007',
    propertyName: 'Inland Empire Distribution Hub',
    tagline: '485,000 SF Class A Industrial Distribution',
    location: 'Los Angeles, CA 92316',
    fullAddress: '2800 Inland Empire Blvd, Ontario, CA 92316',
    loanAmount: '$72.0M',
    product: 'Senior Mortgage',
    stage: 'Term Sheet Issued',
    propertyType: 'Industrial',
    yearBuilt: 2020,
    totalSF: '485,000',
    stories: 1,
    parking: '180 trailer stalls, 650 car spaces',
    amenities: '36ft Clear Height, ESFR Sprinklers, Cross-Dock, 80+ Dock Doors, Rail Served',
    zoning: 'IL Light Industrial',
    parcel: '0210-211-05-0000',
    lastSale: '$88.2M (2022)',
    photos: PHOTO_SET_LOGISTICS_ALT,
    mapLat: 34.0633,
    mapLng: -117.6509,
    population3mi: '142,500',
    medianHHI: '$62,400',
    popGrowth: '+1.8%',
    walkScore: '28/100',
    crimeIndex: 'Moderate',
    empGrowth: '+2.6%',
    topEmployers: ['Amazon', 'UPS', 'FedEx', 'Target Distribution', 'Walmart Logistics'],
    schools: 'B-rated Ontario-Montclair district',
    transit: 'I-10 0.5mi, I-15 2mi, Ontario International Airport 4mi, BNSF Rail adjacent',
    nearbyDevelopments: '2.8M SF under construction within 3mi radius',
    submarket: 'Inland Empire West Industrial',
    submarketVacancy: '3.2%',
    submarketRentGrowth: '+5.1%',
    submarketAbsorption: '6,400,000 SF/yr',
    revenue: '$7.83M',
    expenses: '$1.49M',
    noi: '$6.34M',
    capRate: '6.16%',
    occupancy: '85.6%',
    expenseRatio: '19.0%',
    revPerUnit: '$16.14/SF',
    expPerUnit: '$3.07/SF',
    noiPerUnit: '$13.07/SF',
    tenants: [
      { tenant: 'Home Depot Supply Chain', suite: 'Bldg 1', sf: 260000, rentMo: 282750, leaseStart: '2021-01', leaseEnd: '2031-12', status: 'Current' },
      { tenant: 'Kuehne+Nagel', suite: 'Bldg 2', sf: 155000, rentMo: 161875, leaseStart: '2022-06', leaseEnd: '2029-05', status: 'Current' },
      { tenant: 'Vacant', suite: 'Bldg 3', sf: 70000, rentMo: 0, leaseStart: '--', leaseEnd: '--', status: 'Vacant' },
    ],
    avgRentSF: '$12.79/SF NNN',
    leaseExpirations: [
      { year: '2029', sf: 155000, pctOfTotal: 32 },
      { year: '2031-2032', sf: 260000, pctOfTotal: 54 },
      { year: 'Vacant', sf: 70000, pctOfTotal: 14 },
    ],
    contacts: [
      { role: 'Property Manager', name: 'Brian Cho', company: 'Rexford Industrial', phone: '(909) 555-0188', email: 'bcho@rexfordindustrial.com' },
      { role: 'Leasing Broker', name: 'Stephanie Wu', company: 'JLL', phone: '(909) 555-0299', email: 'swu@jll.com' },
      { role: 'Insurance Broker', name: 'Mark Gonzalez', company: 'Aon', phone: '(909) 555-0377', email: 'mgonzalez@aon.com' },
    ],
    comparables: [
      { name: 'Haven Gateway Center', sf: 520000, avgRent: 13.20, capRate: 5.2, distance: '2.1mi' },
      { name: 'Rancho Cucamonga Logistics', sf: 380000, avgRent: 12.50, capRate: 5.5, distance: '4.8mi' },
      { name: 'Chino Hills Distribution', sf: 600000, avgRent: 12.80, capRate: 5.3, distance: '6.2mi' },
    ],
    compSummary: 'Rent: In line | Cap Rate: Above comps (vacancy drag) | Strong IE West fundamentals',
    lastInspection: 'Feb 22, 2026',
    inspector: 'AEI Consultants',
    overallCondition: 'Excellent (A)',
    immediateRepairs: '$35,000',
    deferredMaintenance: '$150,000 over 5 years',
    environmental: 'Phase I ESA Complete - No RECs identified',
    seismicZone: 'Moderate (Zone 4 - seismic retrofit complete)',
    floodZone: 'X (Minimal)',
    sponsorId: 'meridian-science',
  },

  'ALG-1011': {
    dealId: 'ALG-1011',
    propertyName: 'The Ellington at Uptown',
    tagline: '288-Unit Class A+ Luxury Multifamily',
    location: 'Dallas, TX 75205',
    fullAddress: '3200 McKinney Ave, Dallas, TX 75205',
    loanAmount: '$52.8M',
    product: 'Senior Mortgage',
    stage: 'IC / Committee Approved',
    propertyType: 'Multifamily',
    yearBuilt: 2021,
    totalUnits: 288,
    totalSF: '316,800',
    avgUnitSize: '1,100 SF',
    stories: 8,
    parking: '432 spaces (1.5:1)',
    amenities: 'Infinity Pool, Sky Lounge, Wine Room, Peloton Studio, Dog Wash, Package Lockers',
    zoning: 'PD-193 Planned Development',
    parcel: '00000658720000000',
    lastSale: '$68.5M (2022)',
    photos: PHOTO_SET_LUXURY_MF,
    mapLat: 32.8058,
    mapLng: -96.7984,
    population3mi: '198,300',
    medianHHI: '$92,100',
    popGrowth: '+2.6%',
    walkScore: '84/100',
    crimeIndex: 'Low',
    empGrowth: '+3.5%',
    topEmployers: ['AT&T', 'Texas Instruments', 'Baylor Scott & White', 'Southwest Airlines', 'Goldman Sachs (regional)'],
    schools: 'A-rated Highland Park ISD',
    transit: 'DART McKinney Ave Trolley 0.1mi, Cityplace Station 0.4mi, Dallas North Tollway 0.6mi',
    nearbyDevelopments: '1 project under construction within 0.5mi (340 units)',
    submarket: 'Uptown Dallas Multifamily',
    submarketVacancy: '5.8%',
    submarketRentGrowth: '+3.2%',
    submarketAbsorption: '2,200 units/yr',
    revenue: '$8.12M',
    expenses: '$3.00M',
    noi: '$5.12M',
    capRate: '6.08%',
    occupancy: '95.5%',
    expenseRatio: '36.9%',
    revPerUnit: '$28,194/yr',
    expPerUnit: '$10,417/yr',
    noiPerUnit: '$17,778/yr',
    unitMix: [
      { type: 'Studio (46)', count: 46, avgRent: 1750, occupancy: 97 },
      { type: '1BR (144)', count: 144, avgRent: 2150, occupancy: 96 },
      { type: '2BR (72)', count: 72, avgRent: 2900, occupancy: 95 },
      { type: '3BR (26)', count: 26, avgRent: 3650, occupancy: 92 },
    ],
    avgRentUnit: '$2,350/mo',
    avgRentSF: '$2.14/SF',
    leaseRenewalRate: '71%',
    avgLeaseTerm: '13 months',
    concessions: '$500 avg move-in',
    leaseExpirations: [
      { year: '2026 H1', sf: 95040, pctOfTotal: 30 },
      { year: '2026 H2', sf: 79200, pctOfTotal: 25 },
      { year: '2027 H1', sf: 63360, pctOfTotal: 20 },
      { year: '2027 H2', sf: 47520, pctOfTotal: 15 },
      { year: '2028+', sf: 31680, pctOfTotal: 10 },
    ],
    contacts: [
      { role: 'Property Manager', name: 'David Kim', company: 'Greystar', phone: '(214) 555-0189', email: 'dkim@greystar.com' },
      { role: 'Leasing Director', name: 'Amanda Foster', company: 'On-site', phone: '(214) 555-0267', email: 'afoster@ellingtonuptown.com' },
      { role: 'Insurance Broker', name: 'Patrick O\'Brien', company: 'USI Insurance', phone: '(214) 555-0345', email: 'pobrien@usi.com' },
    ],
    comparables: [
      { name: 'The Victor at Uptown', units: 320, avgRent: 2280, capRate: 5.2, distance: '0.3mi' },
      { name: 'Azure on the Park', units: 210, avgRent: 2400, capRate: 5.0, distance: '0.7mi' },
      { name: 'The Katy Trail Flats', units: 260, avgRent: 2200, capRate: 5.4, distance: '0.5mi' },
    ],
    compSummary: 'Rent: +3% above avg | Cap Rate: Slightly above | Premium finish level differentiator',
    lastInspection: 'Mar 05, 2026',
    inspector: 'Partner Engineering',
    overallCondition: 'Excellent (A)',
    immediateRepairs: '$42,000',
    deferredMaintenance: '$180,000 over 5 years',
    environmental: 'Phase I ESA Complete - No RECs identified',
    seismicZone: 'Very Low Risk',
    floodZone: 'X (Minimal)',
    sponsorId: 'crescent-multifamily',
  },
}

const PROPERTY_PROFILES: Record<string, PropertyProfile> = {
  ...CORE_PROPERTY_PROFILES,
  ...ANTARCTICA_GAP_PROPERTY_PROFILES,
  ...COMMAND_CENTER_PROPERTY_PROFILES,
}

/** True when a curated demo property/sponsor profile exists for this id. */
export function hasCuratedPropertyProfile(dealId: string): boolean {
  const normalized = dealId.replace(/^AI-/, 'AL-')
  return Boolean(PROPERTY_PROFILES[normalized] ?? PROPERTY_PROFILES[dealId])
}

/* ═══════════════════════════════════════════════════════════════════════════
   SPONSOR PROFILES
   ═══════════════════════════════════════════════════════════════════════════ */

const SPONSOR_PROFILES: Record<string, SponsorProfile> = {
  'harbor-dev-group': {
    id: 'harbor-dev-group',
    companyName: 'Harbor Development Group',
    principal: 'Ricardo Medina',
    founded: 2012,
    hq: 'Miami, FL',
    aum: '$1.8B',
    propertyCount: 28,
    totalUnits: '8,200 units',
    reputationScore: 88,
    dealsWithAntarctica: 2,
    totalVolume: '$94.5M',
    revenueEarned: '$1.4M',
    avgDscrAtOrig: '1.45x',
    defaultHistory: 'None',
    avgDaysToClose: 52,
    firstDeal: 'Jun 2022',
    relationshipTier: 'Gold',
    dealHistory: [
      { dealId: 'ALG-1001', property: 'Harbor Point Residences', amount: '$62.5M', date: 'Mar 2026', rate: 'SOFR+375', status: 'In Process', revenue: '$0' },
      { dealId: 'ALG-0088', property: 'Bayshore Gardens', amount: '$32.0M', date: 'Jun 2022', rate: 'SOFR+310', status: 'Performing', revenue: '$1.4M' },
    ],
    riskFactors: [
      { factor: 'Credit Risk', level: 'LOW', icon: 'check' },
      { factor: 'Concentration', level: 'LOW', icon: 'check' },
      { factor: 'Litigation', level: 'NONE', icon: 'check' },
      { factor: 'Market Risk', level: 'LOW', icon: 'check' },
      { factor: 'Reputation', level: 'EXCELLENT', icon: 'check' },
      { factor: 'Financial Stress', level: 'LOW', icon: 'check' },
    ],
    overallRiskRating: 'LOW',
    overallRiskScore: 88,
    concentrationNote: '5% of sponsor AUM with Antarctica. No concerns.',
    portfolio: [
      { name: 'Harbor Point Residences', type: 'Multifamily', location: 'Miami, FL', units: 340, value: '$89.3M', year: 2019, status: 'Stabilized' },
      { name: 'Bayshore Gardens', type: 'Multifamily', location: 'Miami, FL', units: 220, value: '$52.0M', year: 2017, status: 'Stabilized' },
      { name: 'Coral Gables Terrace', type: 'Multifamily', location: 'Coral Gables, FL', units: 180, value: '$38.5M', year: 2020, status: 'Stabilized' },
      { name: 'Coconut Grove Commons', type: 'Mixed-Use', location: 'Miami, FL', units: 95, value: '$42.0M', year: 2021, status: 'Stabilized' },
      { name: 'Key Biscayne Villas', type: 'Multifamily', location: 'Key Biscayne, FL', units: 64, value: '$28.0M', year: 2018, status: 'Stabilized' },
      { name: 'Doral Crossing', type: 'Multifamily', location: 'Doral, FL', units: 310, value: '$72.0M', year: 2022, status: 'Stabilized' },
      { name: 'Brickell Heights II', type: 'Multifamily', location: 'Miami, FL', units: 420, value: '$115.0M', year: 2023, status: 'Lease-Up' },
      { name: 'Aventura Bay', type: 'Multifamily', location: 'Aventura, FL', units: 280, value: '$68.0M', year: 2016, status: 'Stabilized' },
      { name: 'Wynwood Arts District Living', type: 'Mixed-Use', location: 'Miami, FL', units: 150, value: '$55.0M', year: 2024, status: 'Under Construction' },
      { name: 'Fort Lauderdale Marina', type: 'Multifamily', location: 'Fort Lauderdale, FL', units: 200, value: '$48.0M', year: 2015, status: 'Stabilized' },
      { name: 'West Palm Residences', type: 'Multifamily', location: 'West Palm Beach, FL', units: 165, value: '$36.0M', year: 2019, status: 'Stabilized' },
      { name: 'Tampa Riverwalk Apts', type: 'Multifamily', location: 'Tampa, FL', units: 240, value: '$58.0M', year: 2023, status: 'Stabilized' },
    ],
    portfolioMetrics: 'Total Value $1.8B | 28 Properties | 8,200 Units',
    geoBreakdown: 'FL 85%, TX 10%, GA 5%',
    typeBreakdown: 'Multifamily 88%, Mixed-Use 12%',
    principals: [
      { name: 'Ricardo Medina', title: 'CEO & Founder', experience: '22 years in CRE development', previous: ['Related Group', 'Swire Properties'], education: 'MBA Columbia, BS Architecture U of Miami', boards: ['ULI Southeast Florida', 'Miami Downtown Development Authority'] },
      { name: 'Elena Vasquez', title: 'CFO', experience: '16 years in RE finance', previous: ['Goldman Sachs RE', 'Blackstone'], education: 'MBA Wharton, BS Finance NYU', boards: ['NMHC Finance Committee'] },
    ],
    news: [
      { headline: 'Harbor Development Breaks Ground on $115M Brickell Heights II', timeAgo: '3 months ago' },
      { headline: 'Medina Named to South Florida Business Journal\'s Power Leaders in RE', timeAgo: '6 months ago' },
      { headline: 'Harbor Dev Acquires 4.2-Acre Wynwood Site for Mixed-Use Project', timeAgo: '9 months ago' },
      { headline: 'Q3 Results: 97% Portfolio Occupancy, 4.1% Rent Growth', timeAgo: '5 months ago' },
    ],
    netWorth: '$125M',
    liquidity: '$32M',
    leverageRatio: '58%',
    creditLines: '$40M (JPMorgan Chase)',
    guarantorCapacity: 'Strong',
    taxReturns: '2023, 2024 on file',
    pfs: 'Current as of Jan 2026',
  },

  'greenfield-re': {
    id: 'greenfield-re',
    companyName: 'Greenfield RE Partners',
    principal: 'David Kowalski',
    founded: 2005,
    hq: 'Phoenix, AZ',
    aum: '$3.2B',
    propertyCount: 38,
    totalUnits: '18.5M SF',
    reputationScore: 94,
    dealsWithAntarctica: 3,
    totalVolume: '$280M',
    revenueEarned: '$4.2M',
    avgDscrAtOrig: '1.55x',
    defaultHistory: 'None',
    avgDaysToClose: 38,
    firstDeal: 'Sep 2018',
    relationshipTier: 'Platinum',
    dealHistory: [
      { dealId: 'ALG-1003', property: 'Gateway Logistics Center', amount: '$145.0M', date: 'Mar 2026', rate: 'SOFR+350', status: 'In Process', revenue: '$0' },
      { dealId: 'ALG-0072', property: 'Tempe Distribution Park', amount: '$85.0M', date: 'Sep 2024', rate: 'SOFR+285', status: 'Performing', revenue: '$2.8M' },
      { dealId: 'ALG-0041', property: 'Chandler Commerce Center', amount: '$50.0M', date: 'Sep 2018', rate: 'SOFR+310', status: 'Paid Off', revenue: '$1.4M' },
    ],
    riskFactors: [
      { factor: 'Credit Risk', level: 'LOW', icon: 'check' },
      { factor: 'Concentration', level: 'MODERATE', icon: 'warn' },
      { factor: 'Litigation', level: 'NONE', icon: 'check' },
      { factor: 'Market Risk', level: 'LOW', icon: 'check' },
      { factor: 'Reputation', level: 'EXCELLENT', icon: 'check' },
      { factor: 'Financial Stress', level: 'LOW', icon: 'check' },
    ],
    overallRiskRating: 'LOW',
    overallRiskScore: 94,
    concentrationNote: '9% of sponsor AUM with Antarctica. Within guidelines.',
    portfolio: [
      { name: 'Gateway Logistics Center', type: 'Industrial', location: 'Phoenix, AZ', sf: 1240000, value: '$207.1M', year: 2022, status: 'Stabilized' },
      { name: 'Tempe Distribution Park', type: 'Industrial', location: 'Tempe, AZ', sf: 850000, value: '$142.0M', year: 2020, status: 'Stabilized' },
      { name: 'Chandler Commerce Center', type: 'Industrial', location: 'Chandler, AZ', sf: 620000, value: '$98.0M', year: 2017, status: 'Stabilized' },
      { name: 'Mesa Logistics Campus', type: 'Industrial', location: 'Mesa, AZ', sf: 480000, value: '$78.0M', year: 2019, status: 'Stabilized' },
      { name: 'Glendale Flex Industrial', type: 'Industrial', location: 'Glendale, AZ', sf: 320000, value: '$52.0M', year: 2021, status: 'Stabilized' },
      { name: 'I-10 Corridor Logistics', type: 'Industrial', location: 'Goodyear, AZ', sf: 1100000, value: '$185.0M', year: 2023, status: 'Lease-Up' },
      { name: 'Dallas Intermodal Hub', type: 'Industrial', location: 'Dallas, TX', sf: 920000, value: '$155.0M', year: 2021, status: 'Stabilized' },
      { name: 'Houston Ship Channel Park', type: 'Industrial', location: 'Houston, TX', sf: 680000, value: '$110.0M', year: 2020, status: 'Stabilized' },
      { name: 'San Antonio Distribution', type: 'Industrial', location: 'San Antonio, TX', sf: 540000, value: '$85.0M', year: 2018, status: 'Stabilized' },
      { name: 'Las Vegas Logistics Park', type: 'Industrial', location: 'Las Vegas, NV', sf: 750000, value: '$120.0M', year: 2022, status: 'Stabilized' },
      { name: 'Reno E-Commerce Center', type: 'Industrial', location: 'Reno, NV', sf: 420000, value: '$65.0M', year: 2023, status: 'Stabilized' },
      { name: 'Sacramento Cold Storage', type: 'Cold Storage', location: 'Sacramento, CA', sf: 280000, value: '$72.0M', year: 2024, status: 'Under Construction' },
      { name: 'Tucson Air Park Industrial', type: 'Industrial', location: 'Tucson, AZ', sf: 380000, value: '$55.0M', year: 2019, status: 'Stabilized' },
    ],
    portfolioMetrics: 'Total Value $3.2B | 38 Properties | 18.5M SF',
    geoBreakdown: 'AZ 45%, TX 28%, NV 15%, CA 12%',
    typeBreakdown: 'Industrial/Logistics 92%, Cold Storage 5%, Flex 3%',
    principals: [
      { name: 'David Kowalski', title: 'CEO & Managing Partner', experience: '28 years in industrial CRE', previous: ['Prologis', 'DCT Industrial'], education: 'MBA Stanford GSB, BS Civil Engineering ASU', boards: ['NAIOP National', 'SIOR Phoenix Chapter'] },
      { name: 'Rachel Foster', title: 'President & COO', experience: '20 years in logistics RE', previous: ['Liberty Property Trust', 'Dermody Properties'], education: 'MBA Kellogg, BS Finance UC Berkeley', boards: ['ULI Industrial Council'] },
    ],
    news: [
      { headline: 'Greenfield Closes $185M I-10 Corridor Logistics Campus', timeAgo: '2 months ago' },
      { headline: 'Kowalski Keynotes NAIOP I.CON West on Industrial Supply Pipeline', timeAgo: '4 months ago' },
      { headline: 'Greenfield Expands into Cold Storage with Sacramento Project', timeAgo: '7 months ago' },
      { headline: 'Portfolio Hits 96% Occupancy Across 18.5M SF', timeAgo: '3 months ago' },
      { headline: 'Greenfield Named Top 10 Industrial Developer by GlobeSt', timeAgo: '10 months ago' },
    ],
    netWorth: '$240M',
    liquidity: '$58M',
    leverageRatio: '55%',
    creditLines: '$75M (Wells Fargo)',
    guarantorCapacity: 'Very Strong',
    taxReturns: '2023, 2024 on file',
    pfs: 'Current as of Feb 2026',
  },

  'pacific-coast-hospitality': {
    id: 'pacific-coast-hospitality',
    companyName: 'Riviera Hospitality Ventures',
    principal: 'Anton Perez',
    founded: 2010,
    hq: 'Miami, FL',
    aum: '$1.1B',
    propertyCount: 18,
    totalUnits: '3,200 keys',
    reputationScore: 82,
    dealsWithAntarctica: 1,
    totalVolume: '$18.5M',
    revenueEarned: '$0',
    avgDscrAtOrig: '1.22x',
    defaultHistory: 'None',
    avgDaysToClose: 0,
    firstDeal: 'Mar 2026',
    relationshipTier: 'New',
    dealHistory: [
      { dealId: 'ALG-1005', property: 'The Langford Hotel', amount: '$18.5M', date: 'Mar 2026', rate: 'SOFR+600', status: 'In Process', revenue: '$0' },
    ],
    riskFactors: [
      { factor: 'Credit Risk', level: 'MODERATE', icon: 'warn' },
      { factor: 'Concentration', level: 'LOW', icon: 'check' },
      { factor: 'Litigation', level: 'NONE', icon: 'check' },
      { factor: 'Market Risk', level: 'MODERATE', icon: 'warn' },
      { factor: 'Reputation', level: 'EXCELLENT', icon: 'check' },
      { factor: 'Financial Stress', level: 'MODERATE', icon: 'warn' },
    ],
    overallRiskRating: 'MODERATE',
    overallRiskScore: 82,
    concentrationNote: '2% of sponsor AUM with Antarctica. New relationship — enhanced due diligence recommended.',
    portfolio: [
      { name: 'The Langford Hotel', type: 'Hospitality', location: 'Miami, FL', units: 126, value: '$78.0M', year: 1926, status: 'Stabilized' },
      { name: 'South Beach Boutique Hotel', type: 'Hospitality', location: 'Miami Beach, FL', units: 89, value: '$62.0M', year: 2018, status: 'Stabilized' },
      { name: 'Key West Resort & Spa', type: 'Hospitality', location: 'Key West, FL', units: 210, value: '$95.0M', year: 2015, status: 'Stabilized' },
      { name: 'Wynwood Design Hotel', type: 'Hospitality', location: 'Miami, FL', units: 145, value: '$48.0M', year: 2022, status: 'Stabilized' },
      { name: 'Palm Beach Oceanfront', type: 'Hospitality', location: 'Palm Beach, FL', units: 180, value: '$110.0M', year: 2020, status: 'Stabilized' },
      { name: 'Naples Bay Resort', type: 'Hospitality', location: 'Naples, FL', units: 165, value: '$72.0M', year: 2017, status: 'Stabilized' },
      { name: 'St. Augustine Historic Inn', type: 'Hospitality', location: 'St. Augustine, FL', units: 45, value: '$18.0M', year: 2014, status: 'Stabilized' },
      { name: 'Savannah Riverfront Hotel', type: 'Hospitality', location: 'Savannah, GA', units: 220, value: '$85.0M', year: 2021, status: 'Stabilized' },
      { name: 'Charleston Market Hotel', type: 'Hospitality', location: 'Charleston, SC', units: 175, value: '$68.0M', year: 2019, status: 'Stabilized' },
      { name: 'Asheville Mountain Lodge', type: 'Hospitality', location: 'Asheville, NC', units: 95, value: '$32.0M', year: 2023, status: 'Lease-Up' },
      { name: 'Cartagena Beach Resort', type: 'Hospitality', location: 'Cartagena, Colombia', units: 240, value: '$45.0M', year: 2022, status: 'Stabilized' },
      { name: 'San Juan Heritage Hotel', type: 'Hospitality', location: 'San Juan, PR', units: 155, value: '$52.0M', year: 2024, status: 'Under Construction' },
    ],
    portfolioMetrics: 'Total Value $1.1B | 18 Properties | 3,200 Keys',
    geoBreakdown: 'FL 65%, Southeast US 20%, Caribbean/LatAm 15%',
    typeBreakdown: 'Boutique Hotels 55%, Resorts 30%, Historic Properties 15%',
    principals: [
      { name: 'Anton Perez', title: 'CEO & Founder', experience: '20 years in hospitality', previous: ['Four Seasons Hotels', 'LW Hospitality Advisors'], education: 'MBA Cornell (Hotel Admin), BS Hospitality FIU', boards: ['AHLA', 'Visit Florida Board'] },
      { name: 'Carmen Rodriguez', title: 'EVP Operations', experience: '18 years in hotel management', previous: ['Marriott International', 'Rosewood Hotels'], education: 'MBA IE Business School, BA Hospitality Mgmt', boards: ['HSMAI Florida'] },
    ],
    news: [
      { headline: 'Riviera Hospitality Acquires Historic Langford for Autograph Conversion', timeAgo: '18 months ago' },
      { headline: 'Perez Named Hotelier of the Year by Florida Restaurant & Lodging Assn', timeAgo: '8 months ago' },
      { headline: 'Riviera Expands Caribbean Portfolio with San Juan Heritage Project', timeAgo: '5 months ago' },
      { headline: 'South Florida Boutique Hotel Market Sees Record RevPAR', timeAgo: '2 months ago' },
    ],
    netWorth: '$68M',
    liquidity: '$15M',
    leverageRatio: '68%',
    creditLines: '$25M (City National Bank)',
    guarantorCapacity: 'Adequate',
    taxReturns: '2023, 2024 on file',
    pfs: 'Current as of Dec 2025',
  },

  'meridian-science': {
    id: 'meridian-science',
    companyName: 'Pacific Logistics Realty',
    principal: 'Thomas Nakamura',
    founded: 2009,
    hq: 'Los Angeles, CA',
    aum: '$2.8B',
    propertyCount: 32,
    totalUnits: '14.2M SF',
    reputationScore: 91,
    dealsWithAntarctica: 2,
    totalVolume: '$142M',
    revenueEarned: '$2.1M',
    avgDscrAtOrig: '1.58x',
    defaultHistory: 'None',
    avgDaysToClose: 42,
    firstDeal: 'Jan 2021',
    relationshipTier: 'Gold',
    dealHistory: [
      { dealId: 'ALG-1007', property: 'Inland Empire Distribution Hub', amount: '$72.0M', date: 'Feb 2026', rate: 'SOFR+375', status: 'In Process', revenue: '$0' },
      { dealId: 'ALG-0065', property: 'Long Beach Logistics Park', amount: '$70.0M', date: 'Jan 2021', rate: 'SOFR+295', status: 'Performing', revenue: '$2.1M' },
    ],
    riskFactors: [
      { factor: 'Credit Risk', level: 'LOW', icon: 'check' },
      { factor: 'Concentration', level: 'LOW', icon: 'check' },
      { factor: 'Litigation', level: 'NONE', icon: 'check' },
      { factor: 'Market Risk', level: 'LOW', icon: 'check' },
      { factor: 'Reputation', level: 'EXCELLENT', icon: 'check' },
      { factor: 'Financial Stress', level: 'LOW', icon: 'check' },
    ],
    overallRiskRating: 'LOW',
    overallRiskScore: 91,
    concentrationNote: '5% of sponsor AUM with Antarctica. No concerns.',
    portfolio: [
      { name: 'Inland Empire Distribution Hub', type: 'Industrial', location: 'Ontario, CA', sf: 485000, value: '$102.9M', year: 2020, status: 'Stabilized' },
      { name: 'Long Beach Logistics Park', type: 'Industrial', location: 'Long Beach, CA', sf: 720000, value: '$145.0M', year: 2019, status: 'Stabilized' },
      { name: 'San Bernardino Intermodal', type: 'Industrial', location: 'San Bernardino, CA', sf: 950000, value: '$165.0M', year: 2021, status: 'Stabilized' },
      { name: 'Carson Commerce Center', type: 'Industrial', location: 'Carson, CA', sf: 380000, value: '$82.0M', year: 2018, status: 'Stabilized' },
      { name: 'Perris Valley Distribution', type: 'Industrial', location: 'Perris, CA', sf: 620000, value: '$98.0M', year: 2022, status: 'Stabilized' },
      { name: 'Fontana Logistics Hub', type: 'Industrial', location: 'Fontana, CA', sf: 540000, value: '$92.0M', year: 2020, status: 'Stabilized' },
      { name: 'Riverside Air Cargo Ctr', type: 'Industrial', location: 'Riverside, CA', sf: 420000, value: '$75.0M', year: 2023, status: 'Lease-Up' },
      { name: 'Chino Hills Business Park', type: 'Flex', location: 'Chino Hills, CA', sf: 280000, value: '$58.0M', year: 2017, status: 'Stabilized' },
      { name: 'Rancho Cucamonga DC', type: 'Industrial', location: 'Rancho Cucamonga, CA', sf: 650000, value: '$115.0M', year: 2021, status: 'Stabilized' },
      { name: 'Bakersfield Cold Storage', type: 'Cold Storage', location: 'Bakersfield, CA', sf: 180000, value: '$48.0M', year: 2024, status: 'Under Construction' },
      { name: 'Phoenix West Valley Hub', type: 'Industrial', location: 'Phoenix, AZ', sf: 520000, value: '$88.0M', year: 2022, status: 'Stabilized' },
      { name: 'Las Vegas Industrial Campus', type: 'Industrial', location: 'Las Vegas, NV', sf: 380000, value: '$62.0M', year: 2023, status: 'Stabilized' },
    ],
    portfolioMetrics: 'Total Value $2.8B | 32 Properties | 14.2M SF',
    geoBreakdown: 'CA (Inland Empire) 65%, CA (LA/LB) 15%, AZ 12%, NV 8%',
    typeBreakdown: 'Industrial/Logistics 90%, Cold Storage 5%, Flex 5%',
    principals: [
      { name: 'Thomas Nakamura', title: 'CEO & Managing Partner', experience: '25 years in industrial RE', previous: ['CBRE Investment Management', 'LaSalle Investment'], education: 'MBA UCLA Anderson, BS Civil Engineering USC', boards: ['NAIOP SoCal', 'SIOR'] },
      { name: 'Michelle Wang', title: 'CFO', experience: '18 years in RE finance', previous: ['Morgan Stanley RE', 'Starwood Capital'], education: 'MBA Chicago Booth, CFA', boards: ['ICSC Finance Committee'] },
    ],
    news: [
      { headline: 'Pacific Logistics Realty Closes $165M San Bernardino Campus', timeAgo: '4 months ago' },
      { headline: 'Nakamura on IE Industrial Supply: "Demand Continues to Outpace"', timeAgo: '2 months ago' },
      { headline: 'PLR Enters Cold Storage Market with Bakersfield Facility', timeAgo: '8 months ago' },
      { headline: 'Portfolio Achieves 97% Occupancy Across 14.2M SF', timeAgo: '1 month ago' },
    ],
    netWorth: '$195M',
    liquidity: '$48M',
    leverageRatio: '56%',
    creditLines: '$60M (Bank of America)',
    guarantorCapacity: 'Very Strong',
    taxReturns: '2023, 2024 on file',
    pfs: 'Current as of Feb 2026',
  },

  'crescent-multifamily': {
    id: 'crescent-multifamily',
    companyName: 'Crescent Multifamily Advisors',
    principal: 'James Whitfield',
    founded: 2008,
    hq: 'Dallas, TX',
    aum: '$2.4B',
    propertyCount: 42,
    totalUnits: '12,800 units',
    reputationScore: 92,
    dealsWithAntarctica: 4,
    totalVolume: '$187M',
    revenueEarned: '$2.8M',
    avgDscrAtOrig: '1.48x',
    defaultHistory: 'None',
    avgDaysToClose: 45,
    firstDeal: 'Mar 2019',
    relationshipTier: 'Platinum',
    dealHistory: [
      { dealId: 'ALG-1011', property: 'The Ellington at Uptown', amount: '$52.8M', date: 'Mar 2026', rate: 'SOFR+350', status: 'In Process', revenue: '$0' },
      { dealId: 'ALG-1004', property: 'The Sinclair on Peachtree', amount: '$38.5M', date: 'Mar 2026', rate: 'SOFR+375', status: 'In Process', revenue: '$0' },
      { dealId: 'ALG-0078', property: 'The Preston at Knox', amount: '$48.2M', date: 'Jun 2023', rate: 'SOFR+295', status: 'Performing', revenue: '$1.6M' },
      { dealId: 'ALG-0052', property: 'Oaklawn Garden Apts', amount: '$47.5M', date: 'Mar 2019', rate: 'SOFR+310', status: 'Paid Off', revenue: '$1.2M' },
    ],
    riskFactors: [
      { factor: 'Credit Risk', level: 'LOW', icon: 'check' },
      { factor: 'Concentration', level: 'MODERATE', icon: 'warn' },
      { factor: 'Litigation', level: 'NONE', icon: 'check' },
      { factor: 'Market Risk', level: 'LOW', icon: 'check' },
      { factor: 'Reputation', level: 'EXCELLENT', icon: 'check' },
      { factor: 'Financial Stress', level: 'LOW', icon: 'check' },
    ],
    overallRiskRating: 'LOW',
    overallRiskScore: 92,
    concentrationNote: '8% of sponsor AUM with Antarctica. Within guidelines but approaching monitor threshold.',
    portfolio: [
      { name: 'The Ellington at Uptown', type: 'Multifamily', location: 'Dallas, TX', units: 288, value: '$84.2M', year: 2021, status: 'Stabilized' },
      { name: 'The Sinclair on Peachtree', type: 'Multifamily', location: 'Atlanta, GA', units: 230, value: '$55.0M', year: 2016, status: 'Stabilized' },
      { name: 'The Preston at Knox', type: 'Multifamily', location: 'Dallas, TX', units: 320, value: '$92.0M', year: 2019, status: 'Stabilized' },
      { name: 'Oaklawn Garden Apartments', type: 'Multifamily', location: 'Dallas, TX', units: 260, value: '$68.0M', year: 2015, status: 'Stabilized' },
      { name: 'Bishop Arts Lofts', type: 'Multifamily', location: 'Dallas, TX', units: 180, value: '$48.0M', year: 2020, status: 'Stabilized' },
      { name: 'Deep Ellum Studios', type: 'Mixed-Use', location: 'Dallas, TX', units: 150, value: '$42.0M', year: 2022, status: 'Stabilized' },
      { name: 'Plano Town Center Living', type: 'Multifamily', location: 'Plano, TX', units: 340, value: '$82.0M', year: 2018, status: 'Stabilized' },
      { name: 'Frisco Heights', type: 'Multifamily', location: 'Frisco, TX', units: 420, value: '$105.0M', year: 2023, status: 'Lease-Up' },
      { name: 'Fort Worth Magnolia', type: 'Multifamily', location: 'Fort Worth, TX', units: 280, value: '$65.0M', year: 2017, status: 'Stabilized' },
      { name: 'Austin Domain Apts', type: 'Multifamily', location: 'Austin, TX', units: 310, value: '$88.0M', year: 2021, status: 'Stabilized' },
      { name: 'San Antonio River Walk Res', type: 'Multifamily', location: 'San Antonio, TX', units: 220, value: '$52.0M', year: 2019, status: 'Stabilized' },
      { name: 'Buckhead Terrace', type: 'Multifamily', location: 'Atlanta, GA', units: 195, value: '$55.0M', year: 2020, status: 'Stabilized' },
      { name: 'Nashville Gulch Tower', type: 'Mixed-Use', location: 'Nashville, TN', units: 175, value: '$62.0M', year: 2024, status: 'Under Construction' },
      { name: 'Charlotte SouthEnd Living', type: 'Multifamily', location: 'Charlotte, NC', units: 250, value: '$58.0M', year: 2022, status: 'Stabilized' },
      { name: 'Scottsdale Waterfront', type: 'Senior Living', location: 'Scottsdale, AZ', units: 140, value: '$45.0M', year: 2023, status: 'Stabilized' },
    ],
    portfolioMetrics: 'Total Value $2.4B | 42 Properties | 12,800 Units',
    geoBreakdown: 'TX 45%, GA 15%, FL 10%, TN 10%, NC 10%, AZ 10%',
    typeBreakdown: 'Multifamily 70%, Mixed-Use 20%, Senior Living 10%',
    principals: [
      { name: 'James Whitfield', title: 'CEO & Founder', experience: '25 years in multifamily', previous: ['Trammell Crow Residential', 'Hines'], education: 'MBA Wharton, BS Finance SMU', boards: ['ULI', 'NMHC', 'Dallas RE Council'] },
      { name: 'Sarah Mitchell', title: 'CFO', experience: '18 years in RE finance', previous: ['CBRE', 'JLL Capital Markets'], education: 'CPA, CFA, MBA UT Austin', boards: ['NMHC Finance Committee'] },
    ],
    news: [
      { headline: 'Crescent Multifamily Closes $340M Dallas Portfolio Recapitalization', timeAgo: '2 months ago' },
      { headline: 'Whitfield Named Top 40 Under 40 in CRE by Globe St', timeAgo: '6 months ago' },
      { headline: 'Crescent Expands into Arizona with Scottsdale Senior Living', timeAgo: '8 months ago' },
      { headline: 'Q4 Earnings: 12% Portfolio Growth, 96% Occupancy', timeAgo: '4 months ago' },
      { headline: 'Nashville Gulch Tower Breaks Ground — $62M Mixed-Use', timeAgo: '3 months ago' },
    ],
    netWorth: '$180M',
    liquidity: '$45M',
    leverageRatio: '62%',
    creditLines: '$50M (JPMorgan)',
    guarantorCapacity: 'Strong',
    taxReturns: '2023, 2024 on file',
    pfs: 'Current as of Jan 2026',
  },

  'helios-demo-sponsor': {
    id: 'helios-demo-sponsor',
    companyName: 'Helios Demo Sponsor (Illustrative)',
    principal: 'Platform Demo Team',
    founded: 2015,
    hq: 'New York, NY',
    aum: '$4.2B',
    propertyCount: 62,
    totalUnits: 'Diversified (demo)',
    reputationScore: 85,
    dealsWithAntarctica: 6,
    totalVolume: '$890M',
    revenueEarned: '$12.4M',
    avgDscrAtOrig: '1.38x',
    defaultHistory: 'None (demo book)',
    avgDaysToClose: 48,
    firstDeal: 'Jan 2018',
    relationshipTier: 'Gold',
    dealHistory: [
      { dealId: 'AL-2025-001', property: 'One Liberty Plaza', amount: '$245.0M', date: 'Mar 2026', rate: 'SOFR+185', status: 'In Process', revenue: '$0' },
      { dealId: 'AL-2025-002', property: 'Parkway Industrial Portfolio', amount: '$178.0M', date: 'Mar 2026', rate: 'SOFR+155', status: 'In Process', revenue: '$0' },
      { dealId: 'ALG-1002', property: 'Pinnacle Tower', amount: '$47.0M', date: 'Mar 2026', rate: 'Fixed 5.85%', status: 'In Process', revenue: '$0' },
    ],
    riskFactors: [
      { factor: 'Credit Risk', level: 'LOW', icon: 'check' },
      { factor: 'Concentration', level: 'MODERATE', icon: 'warn' },
      { factor: 'Litigation', level: 'NONE', icon: 'check' },
      { factor: 'Market Risk', level: 'LOW', icon: 'check' },
      { factor: 'Reputation', level: 'EXCELLENT', icon: 'check' },
      { factor: 'Financial Stress', level: 'LOW', icon: 'check' },
    ],
    overallRiskRating: 'LOW',
    overallRiskScore: 85,
    concentrationNote: 'Representative sponsor used for auto-generated Command Center / gap demo profiles.',
    portfolio: [
      { name: 'Metro Core Office', type: 'Office', location: 'New York, NY', sf: 850000, value: '$392.0M', year: 1972, status: 'Stabilized' },
      { name: 'Sunbelt Logistics Park', type: 'Industrial', location: 'Dallas, TX', sf: 2400000, value: '$305.8M', year: 2019, status: 'Stabilized' },
      { name: 'Gateway Life Science', type: 'Lab/R&D', location: 'Boston, MA', sf: 680000, value: '$559.6M', year: 2022, status: 'Stabilized' },
    ],
    portfolioMetrics: 'Illustrative portfolio metrics for UI demonstration only.',
    geoBreakdown: 'National (demo)',
    typeBreakdown: 'Office / Industrial / Multifamily / Other (demo)',
    principals: [
      { name: 'Jordan Ellis', title: 'Managing Partner', experience: '18 years institutional CRE', previous: ['Global Investment Manager', 'Money Center Bank'], education: 'MBA, CFA (demo)', boards: ['ULI', 'NMHC (demo)'] },
      { name: 'Taylor Brooks', title: 'Head of Asset Management', experience: '14 years operations', previous: ['National PM Platform'], education: 'BS Real Estate Finance (demo)', boards: ['CREW (demo)'] },
    ],
    news: [
      { headline: 'Helios demo data refresh — sponsor narrative for generated property profiles', timeAgo: '1 week ago' },
      { headline: 'Platform showcases end-to-end relationship view (illustrative)', timeAgo: '2 weeks ago' },
    ],
    netWorth: '$210M',
    liquidity: '$38M',
    leverageRatio: '54%',
    creditLines: '$75M (demo)',
    guarantorCapacity: 'Strong',
    taxReturns: 'Demo — not production PII',
    pfs: 'Demo — illustrative only',
  },
}

/* ═══════════════════════════════════════════════════════════════════════════
   HELPER COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DIALOG
   ═══════════════════════════════════════════════════════════════════════════ */

function RedirectLiveLoanToDossier({ dealId }: { dealId: string }) {
  const closeProfile = usePropertyProfileStore((s) => s.closeProfile)
  useEffect(() => {
    useDealDossierStore.getState().openDossier(dealId)
    closeProfile()
  }, [dealId, closeProfile])
  return null
}

export default function PropertyProfileDialog() {
  const { isOpen, dealId, activeTab, closeProfile, setTab } = usePropertyProfileStore()

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeProfile()
    }
    if (isOpen) window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, closeProfile])

  if (!isOpen || !dealId) return null

  // Normalize common typo (AI vs AL) and resolve profile
  const normalizedDealId = dealId.replace(/^AI-/, 'AL-')
  const profile = PROPERTY_PROFILES[normalizedDealId]
  const sponsorId = profile?.sponsorId
  const sponsor = sponsorId ? SPONSOR_PROFILES[sponsorId] : null

  // Live loans always open Deal Dossier — never the old narratives-only shell.
  if (!profile) {
    return <RedirectLiveLoanToDossier dealId={dealId} />
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={closeProfile}>
      <div
        className="relative flex flex-col rounded-xl border border-amber-400/15 bg-[#090909] shadow-2xl"
        style={{ width: '95%', height: '90%', maxWidth: 1440 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Top bar: Tabs + Close ──────────────────────── */}
        <div className="flex items-center justify-between border-b border-amber-400/10 px-4 flex-shrink-0" style={{ height: 44 }}>
          <div className="flex items-center gap-1">
            {(['property', 'sponsor', 'narratives'] as const).map((tab) => {
              const label =
                tab === 'property' ? 'Property Profile'
                : tab === 'sponsor' ? 'Sponsor / Developer'
                : 'UW Narratives'
              return (
                <button
                  key={tab}
                  onClick={() => setTab(tab)}
                  className={`px-3 py-2 text-[12px] font-medium rounded-t transition-colors relative ${
                    activeTab === tab
                      ? 'text-amber-300'
                      : 'text-stone-500 hover:text-stone-300'
                  }`}
                >
                  {label}
                  {activeTab === tab && (
                    <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-amber-400 rounded-t" />
                  )}
                </button>
              )
            })}
          </div>
          <button
            onClick={closeProfile}
            className="rounded-md p-1.5 text-stone-500 hover:text-stone-200 hover:bg-white/5 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Content ──────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {activeTab === 'property' && <PropertyTab profile={profile} />}
          {activeTab === 'narratives' && <NarrativesPanel dealId={dealId} />}
          {activeTab === 'sponsor' && sponsor && <SponsorTab sponsor={sponsor} />}
          {activeTab === 'sponsor' && !sponsor && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Users size={32} className="mx-auto text-stone-600 mb-3" />
                <p className="text-stone-400 text-sm">Sponsor profile not yet available for this deal.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
